import { useMemo, useState } from 'react'
import { createSearchParams, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import Typography from '@mui/material/Typography'
import { FieldArray } from 'react-final-form-arrays'
import { Form, FormValues } from 'pagesNew/OrginizeBlogPages/OrganizeCreateSuccessStory/Form'
import { PageLoader } from 'components/PageLoader'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { FIND_ONE_STUDY_GUIDE_SECTION, UPDATE_STUDY_GUIDE_SECTION } from 'queries/studyGuide'
import {
  findOneStudyGuideSection,
  findOneStudyGuideSectionVariables,
} from '__generatedTypes__/findOneStudyGuideSection'
import { updateStudyGuideSection, updateStudyGuideSectionVariables } from '__generatedTypes__/updateStudyGuideSection'
import { QUERY_PARAM, STUDY_GUIDE_TAB } from 'pagesNew/BlogPages'
import { findAllStudyGuide_findAllStudyGuide_items as StudyGuide } from '__generatedTypes__/findAllStudyGuide'
import { validateRequiredField } from 'utils/validation'
import { toast } from 'utils/toast'
import { useBroadcastChannel } from 'hooks/useBroadcastChannel'
import { BROADCAST_CHANNEL } from 'constants/consts'
import { REFETCH_STUDY_GUIDES_MESSAGE } from 'pagesNew/StudyGuide/Create'
import {
  Props as StudyGuidesFieldArrayProps,
  StudyGuidesFieldArray,
} from '../OrganizeCreateStudyGuide/Form/StudyGuidesFieldArray'

type Props = {
  platform: Platform
}

export const OrganizeEditStudyGuide = withPlatformCheck(({ platform }: Props) => {
  const { id: sectionId } = useParams()
  const [isPlatformMatch, setIsPlatformMatch] = useState(true)
  const { navigate } = useLink()

  const { data, loading } = useQuery<findOneStudyGuideSection, findOneStudyGuideSectionVariables>(
    FIND_ONE_STUDY_GUIDE_SECTION,
    {
      variables: {
        findOneStudyGuideSectionId: sectionId || '',
      },
      skip: !sectionId,
      onCompleted: (data) => {
        const studyGuideSection = data?.findOneStudyGuideSection

        setIsPlatformMatch(studyGuideSection?.platform.id === platform.id)
      },
    }
  )

  const [updateStudyGuideSection] = useMutation<updateStudyGuideSection, updateStudyGuideSectionVariables>(
    UPDATE_STUDY_GUIDE_SECTION
  )

  const handleSubmit = async (values: FormValues) => {
    try {
      const { id, title, description = '' } = values
      await updateStudyGuideSection({
        variables: {
          updateStudyGuideSectionInputFieldName: {
            id,
            title,
            description,
            platform: {
              id: platform.id,
            },
            studyGuide: values.articles.map(({ id }) => ({ id })),
            studyGuideIds: values.articles.map(({ id }) => id),
          },
        },
      })

      navigate(cmsRoutes.BLOG_PAGES, {}, { searchParams: createSearchParams({ [QUERY_PARAM]: STUDY_GUIDE_TAB.id }) })
      toast.success('Study guide section updated successfully')
    } catch (error) {
      toast.error('Failed to update study guide section')
    }
  }

  useBroadcastChannel(BROADCAST_CHANNEL, (event) => {
    if (event.data === REFETCH_STUDY_GUIDES_MESSAGE) {
      toast.success('Study guide created successfully')
    }
  })

  const initialValues: FormValues = useMemo(() => {
    const studyGuides = data?.findOneStudyGuideSection?.studyGuide || []

    const articlesOrder: FormValues['articles'] =
      (data?.findOneStudyGuideSection?.studyGuideIds
        .map((id) => studyGuides.find(({ id: studyGuideId }) => id === studyGuideId))
        .filter((studyGuide) => !!studyGuide) as unknown as FormValues['articles']) || []

    return {
      id: sectionId,
      title: data?.findOneStudyGuideSection?.title || '',
      description: data?.findOneStudyGuideSection?.description || '',
      articles: articlesOrder,
    }
  }, [loading])

  if (loading) {
    return <PageLoader />
  }

  if (!isPlatformMatch) {
    return (
      <Typography>
        You are trying to edit study guide section with different platform (platform in url: {platform.title}, success
        story platform: {data?.findOneStudyGuideSection?.platform?.title})
      </Typography>
    )
  }

  return (
    <Form
      platform={platform}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      title="Organize Study Guide Articles"
      createLink={cmsRoutes.CREATE_STUDY_GUIDE}
      renderFields={({ form, linkParams }) => (
        <FieldArray<StudyGuide> name="articles" validate={validateRequiredField}>
          {({ fields, meta }) => (
            <StudyGuidesFieldArray
              platform={platform}
              onChange={form.change as unknown as StudyGuidesFieldArrayProps['onChange']}
              fields={fields}
              meta={meta}
              linkParams={linkParams}
            />
          )}
        </FieldArray>
      )}
    />
  )
})
